'use client'

import * as React from 'react'
import { useState, useEffect } from 'react'
import { cn } from '../../utils/cn'
import { Menu, X, ChevronDown } from 'lucide-react'

interface NavLink {
  label: string
  href: string
  children?: NavLink[]
}

interface NavbarProps {
  logo: React.ReactNode
  logoHref?: string
  links: NavLink[]
  ctaText?: string
  ctaHref?: string
  ctaElement?: React.ReactNode
  transparent?: boolean
}

function NavDropdown({ link }: { link: NavLink }) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        className={cn(
          'flex items-center gap-1 px-3 py-2 text-sm font-medium',
          'text-[var(--landing-text-secondary)] hover:text-[var(--landing-text-primary)]',
          'transition-colors duration-150'
        )}
      >
        {link.label}
        <ChevronDown className={cn(
          'h-4 w-4 transition-transform duration-150',
          isOpen && 'rotate-180'
        )} />
      </button>

      {isOpen && link.children && (
        <div
          className={cn(
            'absolute top-full left-0 mt-1 min-w-48 py-2',
            'bg-[var(--landing-bg-card)] border border-[var(--landing-border)]',
            'rounded-lg shadow-xl',
            'animate-in fade-in-0 slide-in-from-top-2 duration-150'
          )}
        >
          {link.children.map((child) => (
            <a
              key={child.href}
              href={child.href}
              className={cn(
                'block px-4 py-2 text-sm',
                'text-[var(--landing-text-secondary)] hover:text-[var(--landing-text-primary)]',
                'hover:bg-[var(--landing-bg-hover)]',
                'transition-colors duration-150'
              )}
            >
              {child.label}
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

export function Navbar({
  logo,
  logoHref = '/',
  links,
  ctaText,
  ctaHref,
  ctaElement,
  transparent = true
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 h-16',
        'transition-all duration-200',
        transparent && !isScrolled
          ? 'bg-transparent'
          : 'bg-[var(--landing-bg)]/80 backdrop-blur-lg border-b border-[var(--landing-border)]'
      )}
    >
      <nav className="landing-container h-full flex items-center">
        {/* Logo - fixed width for balance */}
        <a href={logoHref} className="flex-shrink-0">
          {logo}
        </a>

        {/* Desktop Navigation - centered */}
        <div className="hidden md:flex items-center gap-1 flex-1 justify-center">
          {links.map((link) => (
            link.children ? (
              <NavDropdown key={link.href} link={link} />
            ) : (
              <a
                key={link.href}
                href={link.href}
                className={cn(
                  'px-3 py-2 text-sm font-medium',
                  'text-[var(--landing-text-secondary)] hover:text-[var(--landing-text-primary)]',
                  'transition-colors duration-150'
                )}
              >
                {link.label}
              </a>
            )
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-4 flex-shrink-0">
          {ctaElement ? ctaElement : (
            <a
              href={ctaHref}
              className={cn(
                'inline-flex items-center justify-center px-4 py-2',
                'text-sm font-medium rounded-lg',
                'bg-[var(--accent)] text-white',
                'hover:bg-[var(--accent-hover)]',
                'transition-colors duration-150',
                'cta-glow'
              )}
            >
              {ctaText}
            </a>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden p-2 text-[var(--landing-text-secondary)]"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className={cn(
            'md:hidden absolute top-16 left-0 right-0',
            'bg-[var(--landing-bg)] border-b border-[var(--landing-border)]',
            'animate-in slide-in-from-top-2 duration-200'
          )}
        >
          <div className="landing-container py-4 space-y-2">
            {links.map((link) => (
              <div key={link.href}>
                {link.children ? (
                  <div className="space-y-1">
                    <span className="block px-3 py-2 text-sm font-medium text-[var(--landing-text-muted)]">
                      {link.label}
                    </span>
                    {link.children.map((child) => (
                      <a
                        key={child.href}
                        href={child.href}
                        className="block px-6 py-2 text-sm text-[var(--landing-text-secondary)]"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {child.label}
                      </a>
                    ))}
                  </div>
                ) : (
                  <a
                    href={link.href}
                    className="block px-3 py-2 text-sm font-medium text-[var(--landing-text-secondary)]"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                )}
              </div>
            ))}
            <div className="pt-4 px-3">
              {ctaElement ? (
                <div onClick={() => setIsMobileMenuOpen(false)}>
                  {ctaElement}
                </div>
              ) : (
                <a
                  href={ctaHref}
                  className={cn(
                    'block w-full text-center px-4 py-2',
                    'text-sm font-medium rounded-lg',
                    'bg-[var(--accent)] text-white'
                  )}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {ctaText}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
